
import { Component, OnDestroy, OnInit } from '@angular/core';

import { BehaviorSubject, distinctUntilChanged, Subscription, switchMap, tap } from 'rxjs';

import { WireRegion } from '../../../../api/wire/enums/wire-region.enum';
import { DropdownSelectorOptions } from '../../../../Common/Forms/components/ListFilterSelect/list-filter-select.component';
import { DistributionSummaryModel } from '../../../../Common/TargetingShared/model/distribution-summary-model';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AmplifyContentService } from '../../../AmplifyShared/services/amplify-content.service';
import { WireListItemModel } from '../../models/wire-list-item.model';
import { WireService } from '../../services/wire.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';


enum ContentTypeEnum {
    newswire,
    distribution
}

type ListItemModel = WireListItemModel | DistributionSummaryModel;

@Component({
    selector: 'agpr-wire-create-dialog',
    templateUrl: './wire-create-dialog.component.html',
    styleUrls: ['./wire-create-dialog.component.scss'],
    standalone: false
})
export class WireCreateDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    readonly messageSeverities = messageSeverityEnum;
    readonly wireRegionEnum = WireRegion;
    readonly contentTypeOptions: DropdownOptionModel<ContentTypeEnum>[] = [
        new DropdownOptionModel('Newswire', ContentTypeEnum.newswire),
        new DropdownOptionModel('Email Distribution', ContentTypeEnum.distribution),
    ];
    readonly buttonTypes = ButtonTypesEnum;

    createFromPrevious: boolean = false;

    listItems: ListItemModel[] = [];
    rowData: ListItemModel[] = [];
    selectedItem: ListItemModel = null;
    selectedWireRegion: WireRegion;
    selectedContentType: DropdownOptionModel<ContentTypeEnum> = this.contentTypeOptions[0];
    contentSelectorOptions: DropdownSelectorOptions = {
        options: this.contentTypeOptions,
        selectedOption: this.selectedContentType
    };

    subscriptions: Subscription[] = [];

    private dropdownChangedSubject$ = new BehaviorSubject<ContentTypeEnum>(this.selectedContentType.value);
    private dropdownChanged$ = this.dropdownChangedSubject$.pipe(
        distinctUntilChanged(),
        tap(type => this.selectedContentType = this.contentTypeOptions.find(option => option.value === type))
    );

    constructor(
        public coreDialogService: CoreDialogService,
        private wireService: WireService,
        private amplifyContentService: AmplifyContentService,
    ) {
        super(coreDialogService, 'WireCreateDialog');
    }


    ngOnInit() {
        const resetSelected$ = this.dropdownChanged$.pipe(
            tap(() => {
                this.selectedItem = null;
            })
        );
        const listItems$ =  this.dropdownChanged$.pipe(
            switchMap(type => {
                if (type === ContentTypeEnum.newswire) {
                    return this.amplifyContentService.$allWires;
                } else {
                    return this.amplifyContentService.$allDistributions;
                }
            })
        );
        this.subscriptions.push(
            resetSelected$.subscribe(),
            listItems$.subscribe(listItems => {
                this.listItems = listItems.slice().sort(
                    (a, b) => {
                        const aSortDate = a.modifiedDate ? +a.modifiedDate : +a.createdDate;
                        const bSortDate = b.modifiedDate ? +b.modifiedDate : +b.createdDate;
                        return bSortDate - aSortDate;
                    }
                );
                this.rowData = this.listItems;
            }),
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    cancelClicked(): void {
        this.closeDialog();
    }

    setCreateFromPrevious(val: boolean) {
        this.createFromPrevious = val;
        if (!this.createFromPrevious) {
            this.selectedItem = null;
        }
    }

    onWireRegionSelected(selectedWire: WireRegion) {
        this.selectedWireRegion = selectedWire;
    }

    onRowSelect(wire: WireListItemModel): void {
        this.selectedItem = wire;
    }

    createWire() {
        if (this.createFromPrevious) {
            if (this.selectedItem === null || !this.selectedContentType) {
                return;
            }

            if (this.selectedContentType.value === ContentTypeEnum.newswire) {
                this.wireService.cloneFromExistingWire(this.selectedItem.id, this.selectedWireRegion);
            } else {
                this.amplifyContentService.convertDistributionToWire(this.selectedItem.id, this.selectedWireRegion);
            }
            this.closeDialog();
        } else {
            this.wireService.createBlankWire(this.selectedWireRegion);
            this.closeDialog();
        }
    }

    selectType(selectedType: ContentTypeEnum) {
        this.dropdownChangedSubject$.next(selectedType);
    }
}
